const MYSTERY_GIFT_PROPERTY = "_mystery_gift";
const MYSTERY_GIFT_PENDING_NOTICE_KEY = "theme:mystery-gift-removed";
const ECC_T = window.ECC_STRINGS || {}; const ECC_AMOUNT = (value) => { const code = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || "EUR"; const whole = Math.round(Number(value) || 0); if (code === "EUR") return "€" + whole; try { return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: "currency", currency: code, maximumFractionDigits: 0 }).format(whole); } catch (error) { return whole + " " + code; } }; const ECC_FILL = (text, value) => String(text).replace(/%AMOUNT%/g, ECC_AMOUNT(value));
const MINIMUM_CART_MESSAGE = ECC_T.minimum || "Minimum cart value of %AMOUNT% required to be eligible for the Mystery Gift.";
const GIFT_LIMIT_MESSAGE = ECC_T.one_per_order || "Only one Mystery Gift is allowed per order.";

class MysteryGiftGuard extends HTMLElement {
  constructor() {
    super();

    this._allowedForms = new WeakSet();
    this._queuedCart = null;
    this._reconciling = false;
    this._removalNoticePending = false;
    this._onSubmitListener = this._onSubmit.bind(this);
    this._onCartChangedListener = this._onCartChanged.bind(this);
    this._onCartRefreshListener = this._onCartRefresh.bind(this);
    this._onPageShowListener = this._onPageShow.bind(this);
  }

  connectedCallback() {
    this._abortController = new AbortController();
    this._cartDrawer = document.getElementById("cart-drawer");

    document.addEventListener("submit", this._onSubmitListener, {
      capture: true,
      signal: this._abortController.signal
    });
    document.addEventListener("cart:change", this._onCartChangedListener, {
      signal: this._abortController.signal
    });
    document.addEventListener("cart:refresh", this._onCartRefreshListener, {
      signal: this._abortController.signal
    });
    window.addEventListener("pageshow", this._onPageShowListener, {
      signal: this._abortController.signal
    });

    if (this._cartDrawer) {
      this._cartDrawerObserver = new MutationObserver(() => {
        this._showRemovalNotice();
        clearTimeout(this._cartDrawerReconcileTimer);
        this._cartDrawerReconcileTimer = setTimeout(() => this._loadAndQueueCart());
      });
      this._cartDrawerObserver.observe(this._cartDrawer, { childList: true, subtree: true });
    }

    if (this._hasStoredRemovalNotice()) {
      this._activateRemovalNotice();
    }

    this._loadAndQueueCart();
  }

  disconnectedCallback() {
    this._abortController?.abort();
    this._cartDrawerObserver?.disconnect();
    clearTimeout(this._cartDrawerReconcileTimer);
    clearTimeout(this._removalNoticeTimer);
  }

  get threshold() {
    const baseThreshold = parseInt(this.getAttribute("threshold"), 10) || 0;
    const currencyRate = Number(window.Shopify?.currency?.rate) || 1;

    return Math.round(baseThreshold * currencyRate);
  }

  async _onSubmit(event) {
    const form = event.target;

    if (!(form instanceof HTMLFormElement) || !form.querySelector("[data-mystery-gift-marker]")) {
      return;
    }

    if (this._allowedForms.has(form)) {
      this._allowedForms.delete(form);
      this._hideProductMessage(form);
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    if (form.dataset.mysteryGiftChecking === "true") {
      return;
    }

    form.dataset.mysteryGiftChecking = "true";
    const submitButton = event.submitter;
    const submitButtons = Array.from(form.elements).filter((element) => element.type === "submit");
    submitButtons.forEach((button) => button.setAttribute("aria-busy", "true"));
    let handedOffToProductForm = false;

    try {
      const cart = await this._fetchCart();
      const giftItems = this._getGiftItems(cart);

      if (giftItems.length > 0) {
        this._showProductMessage(form, GIFT_LIMIT_MESSAGE);
        return;
      }

      if (this._getEligibleSubtotal(cart) < this.threshold) {
        this._showProductMessage(form, ECC_FILL(MINIMUM_CART_MESSAGE, this.threshold / 100));
        return;
      }

      this._allowedForms.add(form);
      handedOffToProductForm = true;

      try {
        if (submitButton instanceof HTMLElement) {
          form.requestSubmit(submitButton);
        } else {
          form.requestSubmit();
        }
      } catch (error) {
        this._allowedForms.delete(form);
        handedOffToProductForm = false;
        throw error;
      }
    } catch (error) {
      form.dispatchEvent(new CustomEvent("cart:error", {
        bubbles: true,
        detail: {
          error: ECC_T.verify_error || "Unable to verify Mystery Gift eligibility. Please try again."
        }
      }));
      console.error(error);
    } finally {
      delete form.dataset.mysteryGiftChecking;

      if (!handedOffToProductForm) {
        submitButtons.forEach((button) => button.removeAttribute("aria-busy"));
      }
    }
  }

  _onCartChanged(event) {
    if (event.detail?.cart) {
      this._queueCart(event.detail.cart);
    }
  }

  _onCartRefresh() {
    this._loadAndQueueCart();
  }

  _onPageShow(event) {
    if (event.persisted) {
      this._loadAndQueueCart();
    }
  }

  async _loadAndQueueCart() {
    try {
      this._queueCart(await this._fetchCart());
    } catch (error) {
      console.error(error);
    }
  }

  _queueCart(cart) {
    this._queuedCart = cart;

    if (!this._reconciling) {
      this._drainCartQueue();
    }
  }

  async _drainCartQueue() {
    this._reconciling = true;

    try {
      while (this._queuedCart) {
        const cart = this._queuedCart;
        this._queuedCart = null;
        await this._reconcileCart(cart);
      }
    } catch (error) {
      console.error(error);
    } finally {
      this._reconciling = false;
    }
  }

  async _reconcileCart(cart) {
    const giftItems = this._getGiftItems(cart);

    if (giftItems.length === 0) {
      return;
    }

    const updates = {};
    const belowThreshold = this._getEligibleSubtotal(cart) < this.threshold;

    if (belowThreshold) {
      giftItems.forEach((item) => {
        updates[item.key] = 0;
      });
    } else {
      giftItems.forEach((item, index) => {
        const allowedQuantity = index === 0 ? 1 : 0;

        if (item.quantity !== allowedQuantity) {
          updates[item.key] = allowedQuantity;
        }
      });
    }

    if (Object.keys(updates).length === 0) {
      return;
    }

    const updatedCart = await this._updateCart(updates);

    if (belowThreshold && updatedCart.item_count > 0) {
      this._activateRemovalNotice();
    } else if (belowThreshold) {
      this._hideRemovalNotice();
    }

    if (window.themeVariables?.settings?.pageType === "cart") {
      window.location.reload();
      return;
    }

    document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
      bubbles: true,
      detail: {
        baseEvent: belowThreshold ? "mystery-gift:removed" : "mystery-gift:normalized",
        cart: updatedCart
      }
    }));
  }

  _getGiftItems(cart) {
    const renderedGiftKeys = new Set(
      Array.from(document.querySelectorAll("[data-mystery-gift-line-key]"))
        .map((lineItem) => lineItem.getAttribute("data-mystery-gift-line-key"))
    );

    return (cart.items || []).filter((item) => {
      return item.properties?.[MYSTERY_GIFT_PROPERTY] === "true" ||
        item.properties?.[MYSTERY_GIFT_PROPERTY] === true ||
        renderedGiftKeys.has(item.key);
    });
  }

  _getEligibleSubtotal(cart) {
    const giftKeys = new Set(this._getGiftItems(cart).map((item) => item.key));
    const nonGiftSubtotal = (cart.items || []).reduce((subtotal, item) => {
      return giftKeys.has(item.key) ? subtotal : subtotal + item.final_line_price;
    }, 0);
    const cartLevelDiscount = (cart.cart_level_discount_applications || []).reduce((total, discount) => {
      return total + discount.total_allocated_amount;
    }, 0);

    return Math.max(nonGiftSubtotal - cartLevelDiscount, 0);
  }

  async _fetchCart() {
    const response = await fetch(`${this._shopifyRoot()}cart.js`, {
      headers: { "Accept": "application/json" },
      cache: "no-store"
    });

    if (!response.ok) {
      throw new Error(ECC_T.cart_load_error || "Unable to load the current cart.");
    }

    return response.json();
  }

  async _updateCart(updates) {
    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", {
      bubbles: true,
      detail: { sections: sectionsToBundle }
    }));

    const response = await fetch(`${this._shopifyRoot()}cart/update.js`, {
      method: "POST",
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        updates,
        sections: sectionsToBundle,
        sections_url: window.location.pathname
      })
    });
    const responseJson = await response.json();

    if (!response.ok) {
      throw new Error(responseJson.description || (ECC_T.cart_update_error || "Unable to update the current cart."));
    }

    return responseJson;
  }

  _showProductMessage(form, message) {
    const messageContainer = form.querySelector("[data-mystery-gift-product-message]");
    const messageText = messageContainer?.querySelector("[data-mystery-gift-product-message-text]");

    if (!messageContainer || !messageText) {
      return;
    }

    clearTimeout(this._productMessageTimer);

    messageText.textContent = message;
    messageContainer.hidden = false;
    messageContainer.scrollIntoView({ behavior: "smooth", block: "center" });

    this._productMessageTimer = setTimeout(() => {
      messageContainer.hidden = true;
    }, 5e3);
  }

  _hideProductMessage(form) {
    const messageContainer = form.querySelector("[data-mystery-gift-product-message]");

    clearTimeout(this._productMessageTimer);

    if (messageContainer) {
      messageContainer.hidden = true;
    }
  }

  _activateRemovalNotice() {
    this._removalNoticePending = true;

    try {
      sessionStorage.setItem(MYSTERY_GIFT_PENDING_NOTICE_KEY, "true");
    } catch (error) {
      // Storage can be unavailable in private browsing mode.
    }

    clearTimeout(this._removalNoticeTimer);
    this._removalNoticeTimer = null;
    this._showRemovalNotice();
  }

  _showRemovalNotice() {
    if (!this._removalNoticePending || !this._cartDrawer) {
      return;
    }

    if (this._cartDrawer.querySelector(".empty-state")) {
      this._hideRemovalNotice();
      return;
    }

    if (this._cartDrawer.querySelector("[data-mystery-gift-line-key]")) {
      return;
    }

    const messageContainer = this._cartDrawer.querySelector("[data-mystery-gift-cart-message]");

    if (!messageContainer) {
      return;
    }

    messageContainer.querySelectorAll("p").forEach((p) => { if (p.textContent.includes("%AMOUNT%")) p.textContent = ECC_FILL(p.textContent, this.threshold / 100); }); messageContainer.hidden = false;
    this._cartDrawer.show?.();

    if (!this._removalNoticeTimer) {
      this._removalNoticeTimer = setTimeout(() => this._hideRemovalNotice(), 5e3);
    }
  }

  _hideRemovalNotice() {
    this._removalNoticePending = false;
    clearTimeout(this._removalNoticeTimer);
    this._removalNoticeTimer = null;
    this._cartDrawer?.querySelectorAll("[data-mystery-gift-cart-message]").forEach((message) => {
      message.hidden = true;
    });

    try {
      sessionStorage.removeItem(MYSTERY_GIFT_PENDING_NOTICE_KEY);
    } catch (error) {
      // Storage can be unavailable in private browsing mode.
    }
  }

  _hasStoredRemovalNotice() {
    try {
      return sessionStorage.getItem(MYSTERY_GIFT_PENDING_NOTICE_KEY) === "true";
    } catch (error) {
      return false;
    }
  }

  _shopifyRoot() {
    return window.Shopify?.routes?.root || "/";
  }
}

if (!window.customElements.get("mystery-gift-guard")) {
  window.customElements.define("mystery-gift-guard", MysteryGiftGuard);
}
